import {Injectable} from "@angular/core";
import {HttpClient, HttpParams} from "@angular/common/http";
import {SubscriptionBillingInterface} from "../../../../shared/interfaces/subscription-billing.interface";
import {SubscriptionModuleEnum} from "../enums/subscription-module.enum";
import {CompanySubscriptionInterface} from "../interfaces/company-subscription.interface";
import {BasicFiltersInterface} from "../../../../shared/interfaces/basic-filters.interface";
import {ListingResponseInterface} from "../../../../shared/interfaces/listing-response.interface";
import {SubscriptionInvoiceInterface} from "../interfaces/subscription-invoice.interface";
import {Store} from "@ngrx/store";
import {selectSubscription} from "../../../../shared/store/store.selectors";
import {CompanyService} from "../../../../shared/services/company.service";
import {CurrentRestaurantSubscriptionInterface} from "../interfaces/current-restaurant-subscription.interface";
import {GetSubscription} from "../../../../shared/store/store.actions";
import {CompanySubscriptionStatusEnum} from "../enums/company-subscription-status.enum";

@Injectable({
    providedIn: 'root'
})
export class SubscriptionService {
    subscription: CurrentRestaurantSubscriptionInterface;
    constructor(
        private http: HttpClient,
        private store: Store,
        private companyService: CompanyService,
    ) {
        this.store.dispatch(GetSubscription());
        this.store.select(selectSubscription).subscribe(result => {
            this.subscription = result;
        })
    }

    hasAccess(module: SubscriptionModuleEnum): boolean|number {
        if (!this.companyService.company) {
            return false;
        }

        if (this.companyService.company.trial_ends_at) {
            return true;
        }

        if ('STATUS' in this.subscription) {
            if (this.subscription.STATUS === CompanySubscriptionStatusEnum.CANCELED) {
                return false;
            }
        }

        return this.hasAccessInCompanySubscription(module);
    }

    hasAccessInCompanySubscription(module: SubscriptionModuleEnum): boolean {
        if (module === SubscriptionModuleEnum.STOCK) {
            module = SubscriptionModuleEnum.INVENTORY;
        }

        if (module === SubscriptionModuleEnum.KDS_APP) {
            module = SubscriptionModuleEnum.KDS;
        }

        if (module === SubscriptionModuleEnum.LOYALTY_PROGRAM || module === SubscriptionModuleEnum.PROMO_CODES) {
            module = SubscriptionModuleEnum.LOYALTY_PROMO_CODES;
        }

        if (module === SubscriptionModuleEnum.QR_MENU || module === SubscriptionModuleEnum.TABLE_ORDERS) {
            module = SubscriptionModuleEnum.QR_MENU_TABLE_ORDER;
        }

        return this.subscription && this.subscription[module];
    }

    loadIntent() {
     return this.http.post<string>(`/api_company/subscription/intent`, {});
    }

    getInvoices(filters: BasicFiltersInterface) {
        const params = new HttpParams({
            fromObject: {
                page: filters.page.toString(),
                limit: filters.limit.toString(),
                sort_by: filters.sort_by,
                sort_dir: filters.sort_dir,
                search: filters.search,
            }
        });
        return this.http.get<ListingResponseInterface<SubscriptionInvoiceInterface>>(`/api_company/company-subscription/invoices`, {params});
    }

    updateCard(data) {
        return this.http.post<boolean>(`/api_company/subscription/card`, data);
    }

    createCompanySubscription(data) {
        return this.http.post(`/api_company/company-subscription`, data);
    }

    changeCompanySubscription(data) {
        return this.http.patch(`/api_company/company-subscription`, data);
    }

    changeCompanySubscriptionType(is_automatic_payment: boolean) {
        return this.http.patch(`/api_company/company-subscription/type`, {is_automatic_payment});
    }

    getCompanySubscription() {
        return this.http.get<CompanySubscriptionInterface>(`/api_company/company-subscription`);
    }

    cancelCompanySubscription() {
        return this.http.post<boolean>(`/api_company/company-subscription/cancel`, {});
    }

    updateRestaurantSubscriptionBilling(restaurantId: number, data: SubscriptionBillingInterface) {
        return this.http.patch<{ data: SubscriptionBillingInterface }>(`/api_company/restaurants/${restaurantId}/subscription-billing`, data);
    }

    deleteRestaurantSubscriptionBilling(restaurantId: number) {
        return this.http.delete<{ data: null }>(`/api_company/restaurants/${restaurantId}/subscription-billing`);
    }
}
